import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, Upload, Download, CloudUpload, CloudOff, Database, RotateCcw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstitutionPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [institution, setInstitution] = useState(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
  });

  const fetchInstitution = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/institution`, { withCredentials: true });
      setInstitution(data);
      setFormData({
        name: data.name || '',
        address: data.address || '',
        phone: data.phone || '',
      });
    } catch (error) {
      toast.error('Error al cargar datos de la institución');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBackups = useCallback(async () => {
    setLoadingBackups(true);
    try {
      const { data } = await axios.get(`${API}/backup/list`, { withCredentials: true });
      setBackups(data.backups || []);
    } catch (error) {
      toast.error('Error al listar backups');
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  const checkDriveStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/drive/status`, { withCredentials: true });
      setDriveConnected(data.connected);
      if (data.connected) {
        fetchBackups();
      }
    } catch (error) {
      console.error('Error checking Drive status:', error);
      toast.error('Error al verificar estado de Google Drive');
    }
  }, [fetchBackups]);

  useEffect(() => {
    fetchInstitution();
    checkDriveStatus();
    
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive conectado exitosamente');
      setSearchParams({});
      setDriveConnected(true);
      fetchBackups();
    }
  }, [fetchInstitution, checkDriveStatus, fetchBackups, searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/institution`, formData, { withCredentials: true });
      toast.success('Datos actualizados correctamente');
      fetchInstitution();
    } catch (error) {
      toast.error('Error al actualizar datos');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo debe ser menor a 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.put(
          `${API}/institution`,
          { logo_base64: reader.result },
          { withCredentials: true }
        );
        toast.success('Logo actualizado');
        fetchInstitution();
      } catch (error) {
        toast.error('Error al subir logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const connectDrive = async () => {
    try {
      const { data } = await axios.get(`${API}/drive/connect`, { withCredentials: true });
      window.location.href = data.authorization_url;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al conectar con Google Drive';
      
      if (error.response?.status === 503) {
        toast.error(
          <div>
            <p className="font-semibold mb-2">Google Drive no configurado</p>
            <p className="text-sm">
              El administrador del sistema debe configurar las credenciales de Google Drive. 
              Mientras tanto, puedes usar el backup manual desde la página de inicio.
            </p>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('¿Está seguro de desconectar Google Drive?')) return;
    try {
      await axios.delete(`${API}/drive/disconnect`, { withCredentials: true });
      toast.success('Google Drive desconectado');
      setDriveConnected(false);
      setBackups([]);
    } catch (error) {
      toast.error('Error al desconectar Drive');
    }
  };

  const createBackup = async () => {
    try {
      setSaving(true);
      const { data } = await axios.post(`${API}/backup/create`, {}, { withCredentials: true });
      toast.success('Backup creado exitosamente en Google Drive');
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear backup');
    } finally {
      setSaving(false);
    }
  };

  const restoreBackup = async (fileId, fileName) => {
    if (!window.confirm(`¿Está seguro de restaurar el backup "${fileName}"? Esto agregará los datos del backup a su base de datos actual.`)) return;
    try {
      setSaving(true);
      const { data } = await axios.post(`${API}/backup/restore/${fileId}`, {}, { withCredentials: true });
      toast.success(`Backup restaurado: ${data.restored.patients} pacientes, ${data.restored.therapists} terapeutas, ${data.restored.reports} informes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar backup');
    } finally {
      setSaving(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
          Datos de la Institución
        </h1>
        <p className="text-sm mt-1" style={{ color: '#718096' }}>
          Gestiona la información y backups de tu institución
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-8">
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
              Logo de la Institución
            </h3>
            <div className="flex items-center gap-6">
              {institution?.logo_base64 ? (
                <img
                  src={institution.logo_base64}
                  alt="Logo"
                  className="w-32 h-32 object-contain border border-[#E5E2DC] rounded-lg p-2"
                />
              ) : (
                <div
                  className="w-32 h-32 flex items-center justify-center border-2 border-dashed border-[#E5E2DC] rounded-lg"
                  style={{ backgroundColor: '#F9F8F6' }}
                >
                  <Building2 size={40} style={{ color: '#718096' }} />
                </div>
              )}
              <div>
                <Label
                  htmlFor="logo-upload"
                  className="btn-primary cursor-pointer inline-flex items-center gap-2"
                >
                  <Upload size={18} />
                  Subir Logo
                </Label>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <p className="text-xs mt-2" style={{ color: '#718096' }}>
                  Formatos: JPG, PNG. Máximo 2MB
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre de la Institución *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1"
              />
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </form>
        </Card>

        <Card className="p-8">
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Backup en Google Drive
          </h3>

          {!driveConnected ? (
            <div className="text-center py-8">
              <CloudOff size={48} className="mx-auto mb-4" style={{ color: '#718096' }} />
              <p className="mb-4" style={{ color: '#718096' }}>
                Conecta tu cuenta de Google Drive para crear backups automáticos de tus datos
              </p>
              <Button onClick={connectDrive} className="btn-primary flex items-center gap-2 mx-auto">
                <CloudUpload size={18} />
                Conectar Google Drive
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <Database size={24} style={{ color: '#4CAF50' }} />
                  <span className="font-medium" style={{ color: '#2D3748' }}>Google Drive Conectado</span>
                </div>
                <Button variant="ghost" size="sm" onClick={disconnectDrive} className="text-red-500">
                  Desconectar
                </Button>
              </div>

              <Button 
                onClick={createBackup} 
                className="btn-primary w-full flex items-center gap-2 justify-center"
                disabled={saving}
              >
                <CloudUpload size={18} />
                {saving ? 'Creando Backup...' : 'Crear Nuevo Backup'}
              </Button>

              <div>
                <h4 className="font-semibold mb-3" style={{ color: '#2D3748' }}>Backups Disponibles</h4>
                {loadingBackups ? (
                  <p className="text-sm" style={{ color: '#718096' }}>Cargando backups...</p>
                ) : backups.length === 0 ? (
                  <p className="text-sm" style={{ color: '#718096' }}>No hay backups disponibles</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {backups.map((backup) => (
                      <div key={backup.id} className="p-3 border border-[#E5E2DC] rounded-lg hover:bg-[#F9F8F6] transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium" style={{ color: '#2D3748' }}>
                              {backup.name}
                            </p>
                            <p className="text-xs" style={{ color: '#718096' }}>
                              {formatDate(backup.created_at)} • {formatBytes(backup.size)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => restoreBackup(backup.id, backup.name)}
                            disabled={saving}
                            className="flex items-center gap-1"
                          >
                            <RotateCcw size={14} />
                            Restaurar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default InstitutionPage;
